import { AnchorButton, Classes, H5 } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { ItemRenderer } from '@blueprintjs/select';
import classNames from 'classnames';
import * as React from 'react';
import { Tooltip } from 'vet-bones/components';
import { noop } from 'vet-bones/utils';
import { SelectItem } from 'vet-bones/utils/select';

import { ItemQueryList } from 'src/components/select/ItemQueryList';
import { ProjectSidebarTileId } from 'src/constants/mosaic';
import {
  TEST_IDS,
  getProjectSidebarSectionTestId,
} from 'src/constants/testIds';
import { isDraftDiagramSelector } from 'src/selectors/diagramSnapshot';
import * as copy from 'src/templates/copy';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

type ProjectSidebarSectionProps<ItemType> = {
  id: ProjectSidebarTileId;
  header: string;
  items: ItemType[];
  itemRenderer: ItemRenderer<ItemType>;
  onAdd: () => void;
  placeholder: string;
};

export const ProjectSidebarSection = <ItemType extends SelectItem>({
  id,
  header,
  items,
  itemRenderer,
  onAdd,
  placeholder,
}: ProjectSidebarSectionProps<ItemType>) => {
  const { projectId } = useRouterParams();
  const isDraftDiagram = useReduxSelector(isDraftDiagramSelector(projectId));

  const SidebarQueryList = ItemQueryList.ofType<ItemType>();
  return (
    <div
      className="sd-sidebar-section"
      data-testid={getProjectSidebarSectionTestId(`${id}`)}
    >
      <SidebarQueryList
        autofocus={false}
        className="sd-sidebar-section-body"
        customRenderer={({ itemList }, queryInputElement) => (
          <div
            className={classNames(
              'sd-sidebar-section',
              'sd-sidebar-section--resources'
            )}
          >
            <div
              className={classNames(
                'sd-sidebar-section-header',
                Classes.ELEVATION_1
              )}
            >
              <div className="sd-sidebar-section-header-row">
                <H5 className="sd-sidebar-section-header-text">{header}</H5>
                <div className="sd-sidebar-section-header-actions">
                  <Tooltip
                    content={copy.components.voicebox.disabledFor[id]}
                    disabled={!isDraftDiagram}
                  >
                    <AnchorButton
                      className="sd-sidebar-header-action"
                      data-testid={TEST_IDS.projectSidebar.addResourceButton}
                      disabled={isDraftDiagram}
                      icon={IconNames.PLUS}
                      minimal
                      onClick={onAdd}
                    />
                  </Tooltip>
                </div>
              </div>
              <div className="sd-sidebar-section-header-controls">
                {queryInputElement}
              </div>
            </div>
            {itemList}
          </div>
        )}
        inputProps={{ placeholder }}
        itemRenderer={itemRenderer}
        items={items}
        noResults=""
        onSelect={noop}
      />
    </div>
  );
};
